import { TrendingUp, TrendingDown, Minus, AlertTriangle, CheckCircle2, AlertOctagon } from "lucide-react";

/**
 * Donnée / État / Tendance badge — the canonical visual for every Noam object.
 *
 * Props:
 *  - data       : raw measured value (formatted by the caller)
 *  - etat       : "bon" | "attention" | "critique"
 *  - tendance   : "hausse" | "stable" | "baisse"
 *  - polarity   : "positive" | "negative"  (determines whether rising = good)
 *  - color      : optional — pre-computed display colour from the backend
 *                 ("vert"|"orange"|"rouge"|"neutre"). If omitted, derived
 *                 locally so the component works without a backend hit too.
 *  - label      : optional caption shown below the badge
 *  - compact    : render a compact pill (no label, smaller padding)
 */
const COLOR_MAP = {
  vert:    { fg: "#1D9E75", bg: "rgba(29,158,117,0.08)",  bd: "rgba(29,158,117,0.30)" },
  orange:  { fg: "#EF9F27", bg: "rgba(239,159,39,0.08)",  bd: "rgba(239,159,39,0.30)" },
  rouge:   { fg: "#E24B4A", bg: "rgba(226,75,74,0.08)",   bd: "rgba(226,75,74,0.30)"  },
  neutre:  { fg: "#A1A1AA", bg: "rgba(161,161,170,0.05)", bd: "rgba(161,161,170,0.20)" },
};

const ETAT_ICON = {
  bon:       CheckCircle2,
  attention: AlertTriangle,
  critique:  AlertOctagon,
};
const TENDANCE_ICON = {
  hausse: TrendingUp,
  baisse: TrendingDown,
  stable: Minus,
};

/** Polarity-aware colour resolver — kept in sync with backend `display_color`. */
function deriveColor(tendance, polarity, etat) {
  const p = (polarity || "negative").startsWith("p") ? "positive" : "negative";
  if (tendance === "stable") {
    return etat === "critique" ? "rouge" : etat === "attention" ? "orange" : "vert";
  }
  const risingIsBad = p === "negative";
  if (tendance === "hausse") return risingIsBad ? "rouge" : "vert";
  return risingIsBad ? "vert" : "rouge"; // baisse
}

export default function DataStateTrendBadge({
  data, etat = "bon", tendance = "stable", polarity = "negative",
  color, label, compact = false,
}) {
  const resolved = color || deriveColor(tendance, polarity, etat);
  const palette = COLOR_MAP[resolved] || COLOR_MAP.neutre;
  const EtatIcon = ETAT_ICON[etat] || CheckCircle2;
  const TrendIcon = TENDANCE_ICON[tendance] || Minus;

  return (
    <div
      className={`inline-flex items-center gap-${compact ? 1.5 : 2} ${compact ? "px-2 py-1" : "px-2.5 py-1.5"} rounded-md border`}
      style={{ background: palette.bg, borderColor: palette.bd, color: palette.fg }}
      data-testid={`dst-badge-${etat}-${tendance}`}
      title={`État: ${etat} · Tendance: ${tendance} · Polarité: ${polarity}`}
    >
      {data !== undefined && data !== null && (
        <span className="font-semibold text-[12px] leading-none">{data}</span>
      )}
      <EtatIcon className="w-3 h-3 shrink-0" />
      <TrendIcon className="w-3 h-3 shrink-0" />
      {!compact && label && (
        <span className="text-[10px] uppercase tracking-wide opacity-80">{label}</span>
      )}
    </div>
  );
}
